import { useEffect } from "react";

/**
 * Selection mode: while active, one capture-phase click handler owns the page.
 * Elements opt in with a data-ask attribute carrying their own description, so
 * this component needs no registry and no knowledge of the dashboard's shape.
 */
export function AskMode({
  active,
  onPick,
  onCancel,
}: {
  active: boolean;
  onPick: (context: string) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!active) return;
    document.body.classList.add("ask-mode");
    const click = (event: MouseEvent) => {
      const target =
        event.target instanceof Element
          ? event.target.closest("[data-ask]")
          : null;
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      onPick(target.getAttribute("data-ask") ?? "");
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("click", click, true);
    document.addEventListener("keydown", key, true);
    return () => {
      document.body.classList.remove("ask-mode");
      document.removeEventListener("click", click, true);
      document.removeEventListener("keydown", key, true);
    };
  }, [active, onPick, onCancel]);

  if (!active) return null;
  return (
    <div className="ask-banner" role="status">
      <span>Click anything to ask about it</span>
      <button type="button" className="button button--small" onClick={onCancel}>
        Cancel (Esc)
      </button>
    </div>
  );
}
